import React, { useState, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { XMarkIcon, CheckIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

interface Stack {
  id: number;
  name: string;
  box_count: number;
}

interface FeedType {
  id: number;
  name: string;
  cost_per_kg: number;
}

interface QuickFeedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  stacks: Stack[];
  feedTypes: FeedType[];
  lastUsed?: any;
}

const QuickFeedModal: React.FC<QuickFeedModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  stacks,
  feedTypes,
  lastUsed
}) => {
  const [selectedStack, setSelectedStack] = useState<number | null>(null);
  const [selectedFeedType, setSelectedFeedType] = useState<number | null>(null);
  const [amount, setAmount] = useState<number>(1.5);
  const [activity, setActivity] = useState<string>('medium');
  const [notes, setNotes] = useState<string>('');
  const [boxRange, setBoxRange] = useState<string>('all');
  const [currentStep, setCurrentStep] = useState<number>(0);

  const stackSelectRef = useRef<HTMLSelectElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  // Предустановленные количества
  const amountPresets = [0.5, 1.0, 1.5, 2.0, 2.5, 3.0];

  // Горячие клавиши
  useHotkeys('escape', onClose, [isOpen]);
  useHotkeys('enter', handleSubmit, [isOpen, selectedStack, selectedFeedType, amount]);
  useHotkeys('tab', handleNextStep, [isOpen, currentStep]);

  useEffect(() => {
    if (isOpen) {
      // Восстанавливаем последние использованные значения
      if (lastUsed) {
        setSelectedStack(lastUsed.stackId || null);
        setSelectedFeedType(lastUsed.feedTypeId || null);
        setAmount(lastUsed.amount || 1.5);
        setActivity(lastUsed.activity || 'medium');
      }
      
      // Фокус на первое поле
      setTimeout(() => {
        stackSelectRef.current?.focus();
      }, 100);
      
      setCurrentStep(0);
    }
  }, [isOpen, lastUsed]);

  function handleNextStep() {
    if (currentStep === 0 && selectedStack) {
      setCurrentStep(1);
      setTimeout(() => amountInputRef.current?.focus(), 50);
    }
  }

  function handleSubmit() {
    if (!selectedStack || !selectedFeedType || !amount) return;

    const feedingData = {
      stack_id: selectedStack,
      feed_type_id: selectedFeedType,
      amount_kg: amount,
      consumption_activity: activity,
      box_range: boxRange,
      notes: notes.trim(),
      feeding_datetime: new Date().toISOString()
    };

    onSubmit(feedingData);
    onClose();
    
    // Сброс формы
    setSelectedStack(null);
    setSelectedFeedType(null);
    setAmount(1.5);
    setActivity('medium');
    setNotes('');
    setBoxRange('all');
    setCurrentStep(0);
  }

  function handleAmountChange(newAmount: number) {
    setAmount(Math.max(0.1, Math.min(10, newAmount)));
  }

  if (!isOpen) return null;

  const selectedStackData = stacks.find(s => s.id === selectedStack);
  const selectedFeedTypeData = feedTypes.find(f => f.id === selectedFeedType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        
        {/* Заголовок */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">🥗 Быстрое кормление</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          
          {/* Прогресс */}
          <div className="flex items-center space-x-2">
            {[0, 1, 2].map((step) => (
              <div
                key={step}
                className={`w-3 h-3 rounded-full ${
                  step <= currentStep ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-2">
              Шаг {currentStep + 1} из 3
            </span>
          </div>

          {/* Выбор стеллажа */}
          <div className={`space-y-2 ${currentStep === 0 ? 'ring-2 ring-blue-500 rounded-lg p-3' : ''}`}>
            <label className="block text-sm font-medium text-gray-700">
              1. Выберите стеллаж
            </label>
            <select
              ref={stackSelectRef}
              value={selectedStack || ''}
              onChange={(e) => {
                setSelectedStack(Number(e.target.value));
                if (e.target.value) handleNextStep();
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Выберите стеллаж --</option>
              {stacks.map((stack) => (
                <option key={stack.id} value={stack.id}>
                  {stack.name} ({stack.box_count} ящиков)
                </option>
              ))}
            </select>
          </div>

          {/* Диапазон ящиков */}
          {selectedStack && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Какие ящики кормить?
              </label>
              <div className="grid grid-cols-1 gap-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="boxRange"
                    value="all"
                    checked={boxRange === 'all'}
                    onChange={(e) => setBoxRange(e.target.value)}
                    className="mr-2"
                  />
                  <span>Весь стеллаж (все {selectedStackData?.box_count} ящиков)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="boxRange"
                    value="range"
                    checked={boxRange === 'range'}
                    onChange={(e) => setBoxRange(e.target.value)}
                    className="mr-2"
                  />
                  <span>Диапазон ящиков (с 1 по 5, например)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="boxRange"
                    value="specific"
                    checked={boxRange === 'specific'}
                    onChange={(e) => setBoxRange(e.target.value)}
                    className="mr-2"
                  />
                  <span>Конкретные ящики (1, 3, 5, 7)</span>
                </label>
              </div>
            </div>
          )}

          {/* Выбор корма */}
          <div className={`space-y-2 ${currentStep === 1 ? 'ring-2 ring-blue-500 rounded-lg p-3' : ''}`}>
            <label className="block text-sm font-medium text-gray-700">
              2. Выберите тип корма
            </label>
            <div className="grid grid-cols-1 gap-2">
              {feedTypes.map((feedType) => (
                <button
                  key={feedType.id}
                  onClick={() => setSelectedFeedType(feedType.id)}
                  className={`p-3 text-left rounded-lg border-2 transition-all ${
                    selectedFeedType === feedType.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{feedType.name}</div>
                  <div className="text-sm text-gray-500">{feedType.cost_per_kg}₽/кг</div>
                </button>
              ))}
            </div>
          </div>

          {/* Количество */}
          <div className={`space-y-2 ${currentStep === 2 ? 'ring-2 ring-blue-500 rounded-lg p-3' : ''}`}>
            <label className="block text-sm font-medium text-gray-700">
              3. Количество корма (кг)
            </label>
            
            {/* Пресеты */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {amountPresets.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setAmount(preset)}
                  className={`p-2 rounded-lg border transition-all ${
                    amount === preset
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {preset} кг
                </button>
              ))}
            </div>

            {/* Точная настройка */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleAmountChange(amount - 0.1)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MinusIcon className="w-4 h-4" />
              </button>
              
              <input
                ref={amountInputRef}
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                step="0.1"
                min="0.1"
                max="10"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              
              <button
                onClick={() => handleAmountChange(amount + 0.1)}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Стоимость */}
            {selectedFeedTypeData && (
              <div className="text-sm text-gray-600">
                Стоимость: {(amount * selectedFeedTypeData.cost_per_kg).toFixed(2)}₽
              </div>
            )}
          </div>

          {/* Активность поедания */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Активность поедания
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'low', label: 'Низкая', color: 'text-red-600' },
                { value: 'medium', label: 'Средняя', color: 'text-yellow-600' },
                { value: 'high', label: 'Высокая', color: 'text-green-600' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setActivity(option.value)}
                  className={`p-2 rounded-lg border transition-all ${
                    activity === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className={option.color}>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Заметки */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Заметки (необязательно)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Дополнительные заметки..."
            />
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Отмена
          </button>
          
          <div className="flex space-x-2">
            <button
              onClick={handleSubmit}
              disabled={!selectedStack || !selectedFeedType || !amount}
              className="flex items-center space-x-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              <span>Сохранить</span>
              <kbd className="px-2 py-1 text-xs bg-black bg-opacity-20 rounded">Enter</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickFeedModal;
